import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Warning as WarningIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const TransactionWarningDialog = ({
  open,
  onClose,
  onProceed,
  warnings = [],
  transactionType = 'confirmation', // 'confirmation' or 'distribution'
  loading = false
}) => {
  const hasWarnings = warnings && warnings.length > 0;

  if (!hasWarnings) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'error';
      case 'open_past':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Active Analysis';
      case 'open_past':
        return 'Open Past Period';
      default:
        return 'Unknown Status';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6" component="span">
            Performance Analysis Warning
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Period Allocation Notice</AlertTitle>
          {transactionType === 'distribution' 
            ? `Some members belong to groups with performance analysis periods that will affect when this payment is allocated.`
            : `This member belongs to a group with a performance analysis period that will affect when this payment is allocated.`
          }
        </Alert>

        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Affected Members:
        </Typography>

        <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          {warnings.map((warning, index) => (
            <React.Fragment key={index}>
              <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                  <PersonIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {warning.member_name}
                      </Typography>
                      <Chip
                        size="small"
                        label={getStatusLabel(warning.period_info?.status)}
                        color={getStatusColor(warning.period_info?.status)}
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {warning.message}
                      </Typography>
                      
                      {warning.period_info && (
                        <Box 
                          sx={{ 
                            mt: 1, 
                            p: 1.5, 
                            bgcolor: 'action.hover', 
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <ScheduleIcon fontSize="small" color="action" />
                            <Typography variant="caption" fontWeight={500} color="text.secondary">
                              Period Details
                            </Typography>
                          </Box>
                          
                          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                            <Typography variant="caption" color="text.secondary">
                              <strong>Period:</strong> {warning.period_info.period_for}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              <strong>Status:</strong> {warning.period_info.status}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              <strong>Start:</strong> {formatDate(warning.period_info.period_start)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              <strong>End:</strong> {formatDate(warning.period_info.period_end)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              {index < warnings.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <InfoIcon fontSize="small" sx={{ mt: 0.1 }} />
            <Box>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                What this means:
              </Typography>
              <Typography variant="body2">
                The payment will be allocated to the specified period date instead of today's date. 
                You can proceed if this is correct, or cancel to resolve the performance analysis status first.
              </Typography>
            </Box>
          </Box>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={onProceed}
          variant="contained"
          color="warning"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Processing...' : 'Proceed Anyway'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionWarningDialog;